import { createSelector, createSlice } from '@reduxjs/toolkit';
import type { RootState } from '@/store/root-state';
import type { HomeownersState } from './homeownersTypes';
import {
  fetchHomeownerById,
  fetchHomeownerDevices,
  fetchHomeownerProperties,
  fetchHomeowners,
  sendPushNotification,
} from './homeownersThunks';
import { logoutUser } from '@/features/auth/authThunks';

const initialState: HomeownersState = {
  byId: {},
  fetchingById: {},
  errorById: {},
  hasFetchedById: {},
  listIds: [],
  isListing: false,
  listError: null,
  hasListed: false,
  nextToken: null,
  totalListed: 0,
  devicesByUserId: {},
  fetchingDevicesByUserId: {},
  devicesErrorByUserId: {},
  hasFetchedDevicesByUserId: {},
  propertiesByUserId: {},
  fetchingPropertiesByUserId: {},
  propertiesErrorByUserId: {},
  hasFetchedPropertiesByUserId: {},
  isSendingPushNotification: false,
  sendPushNotificationError: null,
};

function upsertHomeowners(state: HomeownersState, homeowners: HomeownersState['byId'][string][]) {
  for (const homeowner of homeowners) {
    const id = homeowner.applicationUserId.trim();
    if (!id) continue;

    state.byId[id] = homeowner;
    state.fetchingById[id] = false;
    state.errorById[id] = null;
    state.hasFetchedById[id] = true;

    if (!state.listIds.includes(id)) {
      state.listIds.push(id);
    }
  }
}

const homeownersSlice = createSlice({
  name: 'homeowners',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchHomeowners.pending, (state, action) => {
        state.isListing = true;
        state.listError = null;

        if (!action.meta.arg?.nextToken) {
          state.listIds = [];
          state.nextToken = null;
          state.totalListed = 0;
        }
      })
      .addCase(fetchHomeowners.fulfilled, (state, action) => {
        state.isListing = false;
        upsertHomeowners(state, action.payload.items);
        state.nextToken = action.payload.nextToken;
        state.totalListed = state.listIds.length;
        state.hasListed = true;
      })
      .addCase(fetchHomeowners.rejected, (state, action) => {
        if (action.meta.condition) return;

        state.isListing = false;
        state.listError = action.payload?.message || action.error.message || 'Failed to load home owners';
        state.hasListed = true;
      })
      .addCase(fetchHomeownerById.pending, (state, action) => {
        const id = action.meta.arg.userId.trim();
        if (!id) return;
        state.fetchingById[id] = true;
        state.errorById[id] = null;
      })
      .addCase(fetchHomeownerById.fulfilled, (state, action) => {
        const homeowner = action.payload;
        const id = homeowner.applicationUserId.trim();
        if (!id) return;

        state.byId[id] = homeowner;
        state.fetchingById[id] = false;
        state.errorById[id] = null;
        state.hasFetchedById[id] = true;
      })
      .addCase(fetchHomeownerById.rejected, (state, action) => {
        const id = action.meta.arg.userId.trim();
        if (!id || action.meta.condition) return;

        state.fetchingById[id] = false;
        state.errorById[id] = action.payload?.message || action.error.message || 'Failed to load homeowner';
        state.hasFetchedById[id] = true;
      })
      .addCase(fetchHomeownerDevices.pending, (state, action) => {
        const id = action.meta.arg.userID.trim();
        if (!id) return;

        state.fetchingDevicesByUserId[id] = true;
        state.devicesErrorByUserId[id] = null;
        state.sendPushNotificationError = null;
      })
      .addCase(fetchHomeownerDevices.fulfilled, (state, action) => {
        const id = action.meta.arg.userID.trim();
        if (!id) return;

        state.fetchingDevicesByUserId[id] = false;
        state.devicesByUserId[id] = action.payload.items;
        state.devicesErrorByUserId[id] = null;
        state.hasFetchedDevicesByUserId[id] = true;
      })
      .addCase(fetchHomeownerDevices.rejected, (state, action) => {
        const id = action.meta.arg.userID.trim();
        if (!id || action.meta.condition) return;

        state.fetchingDevicesByUserId[id] = false;
        state.devicesErrorByUserId[id] = action.payload?.message || action.error.message || 'Failed to load devices';
        state.hasFetchedDevicesByUserId[id] = true;
      })
      .addCase(fetchHomeownerProperties.pending, (state, action) => {
        const id = action.meta.arg.userID.trim();
        if (!id) return;

        state.fetchingPropertiesByUserId[id] = true;
        state.propertiesErrorByUserId[id] = null;
      })
      .addCase(fetchHomeownerProperties.fulfilled, (state, action) => {
        const id = action.meta.arg.userID.trim();
        if (!id) return;

        state.fetchingPropertiesByUserId[id] = false;
        state.propertiesByUserId[id] = action.payload;
        state.propertiesErrorByUserId[id] = null;
        state.hasFetchedPropertiesByUserId[id] = true;
      })
      .addCase(fetchHomeownerProperties.rejected, (state, action) => {
        const id = action.meta.arg.userID.trim();
        if (!id || action.meta.condition) return;

        state.fetchingPropertiesByUserId[id] = false;
        state.propertiesErrorByUserId[id] =
          action.payload?.message || action.error.message || 'Failed to load properties';
        state.hasFetchedPropertiesByUserId[id] = true;
      })
      .addCase(sendPushNotification.pending, (state) => {
        state.isSendingPushNotification = true;
        state.sendPushNotificationError = null;
      })
      .addCase(sendPushNotification.fulfilled, (state) => {
        state.isSendingPushNotification = false;
        state.sendPushNotificationError = null;
      })
      .addCase(sendPushNotification.rejected, (state, action) => {
        state.isSendingPushNotification = false;
        state.sendPushNotificationError =
          action.payload?.message || action.error.message || 'Failed to send push notification';
      })
      .addCase(logoutUser.fulfilled, () => ({ ...initialState }));
  },
});

export default homeownersSlice.reducer;

const selectHomeownersState = (state: RootState) => state.homeowners;
const EMPTY_HOMEOWNER_DEVICES: HomeownersState['devicesByUserId'][string] = [];
const EMPTY_HOMEOWNER_PROPERTIES: HomeownersState['propertiesByUserId'][string] = [];

export const selectHomeownerById = (userId?: string) =>
  createSelector(selectHomeownersState, (state) => {
    if (!userId) return null;
    return state.byId[userId.trim()] ?? null;
  });

export const selectIsFetchingHomeownerById = (userId?: string) =>
  createSelector(selectHomeownersState, (state) => {
    if (!userId) return false;
    return state.fetchingById[userId.trim()] ?? false;
  });

export const selectFetchHomeownerByIdError = (userId?: string) =>
  createSelector(selectHomeownersState, (state) => {
    if (!userId) return null;
    return state.errorById[userId.trim()] ?? null;
  });

export const selectHasFetchedHomeownerById = (userId?: string) =>
  createSelector(selectHomeownersState, (state) => {
    if (!userId) return false;
    return state.hasFetchedById[userId.trim()] ?? false;
  });

export const selectHomeownersList = createSelector(selectHomeownersState, (state) =>
  state.listIds
    .map((id) => state.byId[id])
    .filter((homeowner): homeowner is HomeownersState['byId'][string] => Boolean(homeowner)),
);

export const selectHomeownersListLoading = createSelector(selectHomeownersState, (state) => state.isListing);
export const selectHomeownersListError = createSelector(selectHomeownersState, (state) => state.listError);
export const selectHomeownersHasListed = createSelector(selectHomeownersState, (state) => state.hasListed);
export const selectHomeownersNextToken = createSelector(selectHomeownersState, (state) => state.nextToken);
export const selectHomeownersHasMore = createSelector(selectHomeownersState, (state) => Boolean(state.nextToken));
export const selectHomeownersTotalListed = createSelector(selectHomeownersState, (state) => state.totalListed);

export const selectHomeownerDevicesByUserId = (userId?: string) =>
  createSelector(selectHomeownersState, (state) => {
    if (!userId) return EMPTY_HOMEOWNER_DEVICES;
    return state.devicesByUserId[userId.trim()] ?? EMPTY_HOMEOWNER_DEVICES;
  });

export const selectHomeownerDevicesLoadingByUserId = (userId?: string) =>
  createSelector(selectHomeownersState, (state) => {
    if (!userId) return false;
    return state.fetchingDevicesByUserId[userId.trim()] ?? false;
  });

export const selectHomeownerDevicesErrorByUserId = (userId?: string) =>
  createSelector(selectHomeownersState, (state) => {
    if (!userId) return null;
    return state.devicesErrorByUserId[userId.trim()] ?? null;
  });

export const selectHasFetchedHomeownerDevicesByUserId = (userId?: string) =>
  createSelector(selectHomeownersState, (state) => {
    if (!userId) return false;
    return state.hasFetchedDevicesByUserId[userId.trim()] ?? false;
  });

export const selectHomeownerPropertiesByUserId = (userId?: string) =>
  createSelector(selectHomeownersState, (state) => {
    if (!userId) return EMPTY_HOMEOWNER_PROPERTIES;
    return state.propertiesByUserId[userId.trim()] ?? EMPTY_HOMEOWNER_PROPERTIES;
  });

export const selectHomeownerPropertiesLoadingByUserId = (userId?: string) =>
  createSelector(selectHomeownersState, (state) => {
    if (!userId) return false;
    return state.fetchingPropertiesByUserId[userId.trim()] ?? false;
  });

export const selectHomeownerPropertiesErrorByUserId = (userId?: string) =>
  createSelector(selectHomeownersState, (state) => {
    if (!userId) return null;
    return state.propertiesErrorByUserId[userId.trim()] ?? null;
  });

export const selectHasFetchedHomeownerPropertiesByUserId = (userId?: string) =>
  createSelector(selectHomeownersState, (state) => {
    if (!userId) return false;
    return state.hasFetchedPropertiesByUserId[userId.trim()] ?? false;
  });

export const selectIsSendingPushNotification = createSelector(
  selectHomeownersState,
  (state) => state.isSendingPushNotification,
);

export const selectSendPushNotificationError = createSelector(
  selectHomeownersState,
  (state) => state.sendPushNotificationError,
);
